import type { Identifier, XYCoord } from "dnd-core";
import type { FC } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  CaretDownIcon,
  CheckIcon,
  InfoIcon,
  LightCaretRightIcon,
  PlusIcon,
  ReorderIcon,
  XIcon,
} from "../../../../../../components/Icons/Icons";
import { Flex } from "../../../../../../layout/FormLayout";
import React from "react";
import { t, TFunction } from "i18next";
import { Table } from "../../../../../../components/Table/Table";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  EditButton,
  InvisibleButton,
} from "../../../../../../components/Buttons/Buttons";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EditModeTitle } from "../../../../../../components/EditableTitle/EditModeTitle";
import { ButtonGroup } from "../../../../../../components/EditableTitle/EditableTitle";
import axios from "axios";
import type { AxiosError } from "axios";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import {
  removeUnderscore,
  TablePlaceholder,
  toTitleCase,
  useFormWrapper,
  useStoreState,
} from "../../../../../../util/util";
import type { CreatedOption, OptionType } from "../../../../../../types/types";
import type {
  AttributeTemplateSchema,
  TemplateProductFormStepItemAttributeSchema,
  TemplateProductFormStepItemSchema,
  TemplateProductFormStepSchema,
} from "../../../../../../types/types.PIM";
import ReactTooltip from "react-tooltip";
import { useAuthContext } from "../../../../../../components/Auth";
import { ThemeContext } from "styled-components";
import type { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { IndeterminateCheckbox } from "../../../../../../components/CheckBoxes/CheckBoxes";
import { DropDownButton } from "../../../../../../components/DropDownButton/DropDownButton";
import { SoftHeader2 } from "../../../../../../components/Typography/Typography";
import { SearchSelect } from "../../../../../../components/SearchSelect/SearchSelect";
import { DeletableChips } from "../../../../../../components/DeletableChips/DeletableChips";
import { AssetCategory } from "../../SellarAdminPIMAssets/util/AssetsUtil";
import useSWR from "swr";
import { endpoints } from "../../../../../../endpoints";
import { AttributesForm } from "./AttributesForm";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { CollectionForm } from "./CollectionForm";
import { zodRequiredString } from "../../../../../../util/zod.util";
import { z } from "zod";
import {
  borderPrimitiveTokens,
  spacingTokens,
  widthTokens,
} from "../../../../../../designTokens";

export interface CardProps {
  formData: TemplateProductFormStepSchema[];
  card: TemplateProductFormStepSchema;
  index: number;
  template: AttributeTemplateSchema;
  formId?: string;
  stepNames: string[];
  disabled?: boolean;
  moveStep: (dragIndex: number, hoverIndex: number) => void;
  handleAddStep: () => void;
  handleAddDocuments: () => void;
  handleDeleteStep: (stepId: string) => void;
  handleDrop?: () => void;
  handleToggleAllRowsExpanded: (expanded: boolean) => void;
  handleAddItem: (
    step: TemplateProductFormStepSchema,
    items: TemplateProductFormStepItemSchema[]
  ) => void;
  updateDocumentCategories: (
    stepId: string,
    categories: { name: string; id: string }[]
  ) => void;
  moveRow: ({
    item,
    source,
  }: {
    item: TemplateProductFormStepItemSchema;
    source: string;
    data: TemplateProductFormStepItemSchema[];
  }) => void;
  handleExpandedState: (cardId: string, expanded: ExpandedState) => void;
  expandedRows: ExpandedState;
  updateStepItems: ({
    data,
    source,
  }: {
    source: string;
    data: TemplateProductFormStepItemSchema[];
  }) => void;
  handleEditStepName: (stepId: string, name: string) => void;
}
interface DragItem {
  index: number;
  id: string;
  type: string;
}
const ItemTypes = {
  CARD: "card",
};

const StepNameSchemaFn = (t: TFunction, items: string[]) =>
  z.object({
    name: zodRequiredString(t).refine(
      (val) => Boolean(items.indexOf(val.toLocaleLowerCase()) < 0),
      {
        message: t("Duplicate step name not allowed"),
      }
    ),
  });

export const TemplateFormStepCard: FC<CardProps> = ({
  formData,
  card,
  index,
  moveStep,
  moveRow,
  updateStepItems,
  expandedRows,
  handleAddItem,
  handleExpandedState,
  template,
  formId,
  handleAddStep,
  handleAddDocuments,
  handleDeleteStep,
  handleEditStepName,
  handleToggleAllRowsExpanded,
  handleDrop,
  stepNames,
  disabled,
  updateDocumentCategories,
}) => {
  const [tableData, setTableData] = useState(card.items || []);
  const [showTooltip, setShowTooltip] = useState(false);
  const [editStepDisplayName, setEditStepDisplayName] = useState<string>();
  const [documentChips, setDocumentChips] = useState<
    { name: string; id: string }[]
  >([]);
  const [documentOptions, setDocumentOptions] = useState<OptionType[]>([]);
  const theme = useContext(ThemeContext);
  const { notifyError, notifySuccess } = useContext(Notifications);
  const { tenant_id } = useStoreState();
  const ref = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showAddAttributeForm, setShowAddAttributeForm] = useState(false);
  const [showAddCollectionForm, setShowAddCollectionForm] = useState(false);
  const { hasPermission } = useAuthContext();
  const [dropdownItems, setDropdownItems] = useState<string[]>([
    t("Attributes"),
    t("Collection"),
    t("Step"),
    ...(formData.some((form) => form.is_document_step) ? [] : [t("Documents")]),
  ]);
  const [collectionToEdit, setCollectionToEdit] =
    useState<TemplateProductFormStepItemSchema | null>(null);
  const { data: categoryTypeResponse, error: categoryTypeError } =
    useSWR<AssetCategory>(
      `${endpoints.v2_tenants_id_pim_assets_categories(
        tenant_id
      )}?asset_type=document`,
      {
        onSuccess: ({ asset_categories }) =>
          setDocumentOptions(
            asset_categories.map((category) => ({
              label: category.name,
              value: category.id,
            }))
          ),
      }
    );

  const { register, formState, errors } = useFormWrapper({
    defaultValues: {
      document_categories: [],
    },
  });

  const deleteStep = async (id: string) => {
    if (
      template.forms
        .find((form) => form.id === formId)
        ?.steps?.find((step) => step.id === id)
    ) {
      try {
        await axios.delete(
          `v2/tenants/${tenant_id}/templates/${template.template_id}/forms/${formId}/steps/${id}`
        );
        notifySuccess(t("Step has been deleted successfully"));
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage ? errorMessage : t("There was an error deleting step"),
          {
            error,
          }
        );
      }
    } else {
      handleDeleteStep(id);
    }
  };

  const handleAddItemClick = (item: string) => {
    switch (item) {
      case "Attributes":
        setShowAddAttributeForm(true);
        break;
      case "Collection":
        setShowAddCollectionForm(true);
        break;
      case "Step":
        handleAddStep();
        break;
      case "Documents":
        handleAddDocuments();
        break;
    }
  };

  const editNameUseForm = useForm({
    resolver: zodResolver(StepNameSchemaFn(t, stepNames)),
  });

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.CARD,

    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    drop(_item, _monitor) {
      if (handleDrop) {
        handleDrop();
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 3;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveStep(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id: card.id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  preview(drop(ref));
  drag(dragRef);

  const tableColumns = React.useMemo<
    ColumnDef<TemplateProductFormStepItemSchema>[]
  >(() => {
    const handleDeleteItem = (id: string) => {
      const items = card.items?.filter(
        (item) => item.attributes?.[0].id !== id
      );
      setTableData(items || []);
      updateStepItems({ source: card.name, data: items || [] });
    };

    const handleToggleRequired = (
      row: Row<TemplateProductFormStepItemSchema>,
      event: React.FormEvent<HTMLInputElement>
    ) => {
      let newData = tableData;
      if (row.depth === 0) {
        newData[row.index].attributes?.forEach(
          (attr) => (attr.is_required = event.currentTarget.checked)
        );
      } else {
        const parentRowIndex = row.getParentRow()?.index;
        if (parentRowIndex !== undefined && parentRowIndex > -1) {
          const rowAttributes = newData[parentRowIndex].attributes;
          rowAttributes[row.index].is_required = event.currentTarget.checked;
          newData[parentRowIndex].attributes = rowAttributes;
        }
      }
      updateStepItems({ source: card.name, data: newData });
    };
    return [
      {
        header: t("Name"),
        accessorKey: "name",
        cell: ({ row }) => {
          return row.original.type === "group_attribute" ? (
            <>
              <span style={{ display: "inline-block", width: "16px" }} />
              {`${row.original.group_or_collection} / ${row.original.attributes?.[0].name}`}
            </>
          ) : row.getCanExpand() ? (
            <span
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: "pointer", display: "flex" }}
            >
              {row.getIsExpanded() ? (
                <CaretDownIcon width={16} height={16} />
              ) : (
                <LightCaretRightIcon width={16} height={16} />
              )}
              {row.original.group_or_collection}
            </span>
          ) : (
            <>
              {
                (
                  row.original as unknown as TemplateProductFormStepItemAttributeSchema
                ).name
              }
            </>
          );
        },
      },
      {
        header: t("Display Name"),
        accessorKey: "display_name",
        cell: ({ row, renderValue }) => {
          return (
            <>
              {row.depth === 0
                ? row.original.type === "collection_attribute"
                  ? row.original.group_or_collection
                  : row.original.attributes?.[0].display_name
                : renderValue()}
            </>
          );
        },
      },
      {
        header: t("Type"),
        accessorFn: (row) =>
          row.type
            ? row.type
            : (row as unknown as TemplateProductFormStepItemAttributeSchema)
                .input_type,
        cell: (cell) => {
          return toTitleCase(
            removeUnderscore(
              (cell.getValue() as string)?.replace("_attribute", "")
            )
          );
        },
        id: "type",
      },
      {
        header: (cell) => {
          return (
            <span style={{ display: "flex" }}>
              {t("Required")}
              <span
                style={{ marginLeft: "3px" }}
                data-for={`required-${cell.column.id}`}
                data-tip={t(
                  "Required determines which attributes are required."
                )}
              >
                <InfoIcon width={16} height={16} />
              </span>
              <ReactTooltip id={`required-${cell.column.id}`} />
            </span>
          );
        },
        accessorKey: "is_required",
        cell: ({ row }) => (
          <div style={{ display: "inline-block" }}>
            <IndeterminateCheckbox
              {...{
                checked:
                  row.depth === 0
                    ? row.original.attributes?.every((attr) => attr.is_required)
                    : (
                        row.original as unknown as TemplateProductFormStepItemAttributeSchema
                      )?.is_required,
                indeterminate:
                  row.depth === 0
                    ? row.original.attributes?.some(
                        (attr) => !attr.is_required
                      ) &&
                      row.original.attributes?.some((attr) => attr.is_required)
                    : false,
                onChange: (e) => handleToggleRequired(row, e),
              }}
            />
            <ReactTooltip id={`row${row.id}`} />
          </div>
        ),
      },
      ...(hasPermission("modify_templates")
        ? [
            {
              header: t("Actions"),
              accessorKey: "actions",
              cell: ({
                row,
              }: {
                row: Row<TemplateProductFormStepItemSchema>;
              }) => (
                <>
                  {row.depth === 0 && (
                    <div>
                      <ButtonWithConfirmDialog
                        Button={(props) => <DeleteButton {...props} />}
                        testid="remove-step-item"
                        datatip={""}
                        datafor="remove-step-item-tooltip"
                        handleConfirm={() =>
                          handleDeleteItem(row.original.attributes?.[0].id)
                        }
                        confirmMessage={t(
                          "Are you sure you want to delete this item?"
                        )}
                        confirmBtnLabel={t("Delete")}
                        confirmBtnType="destructive"
                        useV2Buttons={true}
                      />
                      {row.original.type === "collection_attribute" && (
                        <EditButton
                          onClick={() => {
                            setShowAddCollectionForm(true);
                            setCollectionToEdit(row.original);
                          }}
                        />
                      )}
                    </div>
                  )}
                </>
              ),
            },
          ]
        : []),
    ];
  }, [tableData, card, updateStepItems, hasPermission]);

  const handleDocumentSelectChange = (data: CreatedOption<string> | null) => {
    if (data) {
      //   setDocumentChips((previous) => [
      //     ...previous,
      //     { name: data.label, id: data.value },
      //   ]);
      // }

      updateDocumentCategories(card.id, [
        ...documentChips,
        { name: data.label, id: data.value },
      ]);
    }
  };

  const handleDocumentChipClick = (name: string) => {
    setDocumentChips(
      (previous) => previous?.filter((item) => item.name !== name) ?? []
    );
    if (!documentOptions.map(({ label }) => `${label}`).includes(name)) {
      setDocumentOptions([
        {
          value: name,
          label: name,
        },
        ...documentOptions,
      ]);
    }
    updateDocumentCategories(
      card.id,
      documentChips.filter((item) => item.name !== name)
    );
  };

  useEffect(() => {
    const items = card?.items;
    setTableData(items || []);
    if (card.is_document_step) {
      setDocumentChips(
        card.categories?.map((category) => ({
          name: category.name,
          id: category.id,
        })) || []
      );
    }
  }, [card]);

  useEffect(() => {
    if (categoryTypeError) {
      notifyError(
        t(
          "There was an error loading document categories, Something went wrong."
        ),
        {
          error: categoryTypeError,
        }
      );
    }
  }, [categoryTypeError, notifyError]);

  useEffect(() => {
    register({ name: "document_categories", required: true });
  }, [register]);

  useEffect(() => {
    setDropdownItems([
      ...(card.is_document_step ? [] : [t("Attributes"), t("Collection")]),
      t("Step"),
      ...(formData.some((form) => form.is_document_step)
        ? []
        : [t("Documents")]),
    ]);
  }, [formData, card.is_document_step]);

  return (
    <div
      ref={ref}
      style={{
        marginBottom: theme.resolve("spacing_3xl"),
        opacity: opacity,
        border: `${borderPrimitiveTokens.border_md} solid ${theme.secondaryBorder}`,
        borderRadius: theme.resolve("radius_md"),
        padding:
          theme.resolve("spacing_2xl") + " " + theme.resolve("spacing_xl"),
      }}
    >
      <Flex
        style={{
          padding: "0px 0 " + theme.resolve("spacing_xl"),
          opacity,
          display: "flex",
          alignItems: "center",
        }}
        data-handler-id={handlerId}
      >
        {hasPermission("modify_templates") && (
          <div
            ref={dragRef}
            style={{ cursor: "move", marginRight: spacingTokens.spacing_2xl }}
          >
            <ReorderIcon />
          </div>
        )}
        <div style={{ width: "100%", display: "flex", alignItems: "center" }}>
          {editStepDisplayName !== card.id && <div>{card.name}</div>}
          {editStepDisplayName === card.id && (
            <Flex>
              <form
                id="edit_step"
                noValidate
                onSubmit={editNameUseForm.handleSubmit(
                  (data: { name: string }) => {
                    handleEditStepName(card.id, data.name);
                    setEditStepDisplayName(undefined);
                  }
                )}
                style={{ position: "relative" }}
              >
                <EditModeTitle
                  name={"name"}
                  theref={inputRef}
                  register={editNameUseForm.register}
                  formState={editNameUseForm.formState}
                  errors={editNameUseForm.errors}
                  tabIndex={0}
                  defaultValue={card.name}
                  fontSize={"regular"}
                  fontWeight={"medium"}
                />
              </form>

              <ButtonGroup>
                <InvisibleButton form={"edit_step"} type="submit">
                  <CheckIcon fill={theme.activeToggleBG} />
                </InvisibleButton>
                <InvisibleButton
                  type="button"
                  onClick={() => setEditStepDisplayName(undefined)}
                >
                  <XIcon fill={theme.destructiveTextColor} />
                </InvisibleButton>
              </ButtonGroup>
            </Flex>
          )}
          {editStepDisplayName !== card.id && !isDragging && (
            <>
              {hasPermission("modify_templates") && (
                <div>
                  <EditButton
                    height={18}
                    id={`edit-step${card.id}`}
                    disabled={disabled}
                    style={{ marginLeft: "10px" }}
                    onClick={() => setEditStepDisplayName(card.id)}
                    datafor={`edit-step${card.id}`}
                    datatip={
                      showTooltip && disabled
                        ? t(
                            "Save your changes first to be able to edit this step"
                          )
                        : undefined
                    }
                  />
                  {showTooltip && disabled && (
                    <ReactTooltip id={`edit-step${card.id}`} />
                  )}
                </div>
              )}
              {hasPermission("modify_templates") && (
                <div
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <ButtonWithConfirmDialog
                    Button={(props) => (
                      <>
                        <DeleteButton {...props} id={`remove-step${card.id}`} />
                      </>
                    )}
                    disabled={stepNames.length === 1}
                    testid={`remove-step${card.id}`}
                    handleConfirm={() => deleteStep(card.id)}
                    confirmMessage={t(
                      "Are you sure you want to delete this step?"
                    )}
                    datatip={
                      stepNames.length === 1
                        ? t("You must have at least one step in the form.")
                        : undefined
                    }
                    datafor={`remove-step${card.id}`}
                  />
                  {stepNames.length === 1 && (
                    <ReactTooltip id={`remove-step${card.id}`} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Flex>
      {card.is_document_step ? (
        <div style={{ position: "relative" }}>
          <SoftHeader2
            style={{
              marginTop: `-${theme.resolve("spacing_md")}`,
              marginLeft: theme.resolve("spacing_4xl"),
              position: "absolute",
              top: "-18px",
              left: 0,
            }}
          >
            {t("Choose which document types are required")}
          </SoftHeader2>
          <div
            style={{
              maxWidth: widthTokens.width_xs,
              marginTop: theme.resolve("spacing_md"),
            }}
          >
            {categoryTypeResponse?.asset_categories.length &&
              categoryTypeResponse?.asset_categories.length > 0 && (
                <SearchSelect
                  name="document_categories"
                  options={categoryTypeResponse?.asset_categories
                    .map((category) => ({
                      label: category.name,
                      value: category.id,
                    }))
                    .filter(
                      (category) =>
                        !documentChips
                          .map((chip) => chip.id)
                          .includes(category.value)
                    )}
                  errors={errors}
                  formState={formState}
                  placeholder={t(`Category`)}
                  value={null}
                  isCreatable={false}
                  onChange={handleDocumentSelectChange}
                />
              )}
            <div style={{ marginTop: theme.resolve("spacing_md") }}>
              <DeletableChips
                chips={documentChips || []}
                noneSelectedName={"Category"}
                handleClick={handleDocumentChipClick}
                invertBG={true}
              />
            </div>
          </div>
          <div style={{ marginTop: theme.resolve("spacing_xl") }}>
            <DropDownButton
              items={dropdownItems}
              textLeft={t("Add")}
              icon={<PlusIcon width={14} height={14} />}
              clickHandler={(item: string) => {
                handleAddItemClick(item);
              }}
            />
          </div>
        </div>
      ) : (
        <>
          <Table
            columns={tableColumns}
            isLoading={false}
            error={undefined}
            subRowsKey={"attributes"}
            expandedRows={!isDragging ? expandedRows : {}}
            handleExpandedState={(e) => handleExpandedState(card.id, e)}
            handleToggleAllRowsExpanded={handleToggleAllRowsExpanded}
            data={tableData}
            Placeholder={
              <TablePlaceholder message={t("No groups or collections added")} />
            }
            enableReorder={hasPermission("modify_templates")}
            lastChildleftAlign={true}
            enableDefaultSort={false}
            name={card.name}
            handleMoveRow={
              hasPermission("modify_templates") ? moveRow : undefined
            }
            handleDrop={
              hasPermission("modify_templates") ? handleDrop : undefined
            }
          />
          <div style={{ marginTop: theme.resolve("spacing_xl") }}>
            <DropDownButton
              items={dropdownItems}
              textLeft={t("Add")}
              icon={<PlusIcon width={14} height={14} />}
              clickHandler={(item: string) => {
                handleAddItemClick(item);
              }}
            />
          </div>
        </>
      )}
      <SlideOut
        title={t("Add Attribute")}
        show={showAddAttributeForm}
        closeFlyout={() => {
          setShowAddAttributeForm(false);
        }}
      >
        <AttributesForm
          templateGroups={template.groups}
          onSuccess={(step, items) => {
            handleAddItem(step, items);
            setShowAddAttributeForm(false);
          }}
          step={card}
          onCancel={() => setShowAddAttributeForm(false)}
        />
      </SlideOut>
      <SlideOut
        title={t("Add Collection")}
        show={showAddCollectionForm}
        closeFlyout={() => {
          setShowAddCollectionForm(false);
          setCollectionToEdit(null);
        }}
      >
        <CollectionForm
          templateCollections={template.collections}
          collectionToEdit={collectionToEdit}
          onSuccess={(step, items) => {
            handleAddItem(step, items);
            setShowAddCollectionForm(false);
            setCollectionToEdit(null);
          }}
          step={card}
          onCancel={() => setShowAddCollectionForm(false)}
        />
      </SlideOut>
    </div>
  );
};
